/**
 * Aiuti delle viste a calendario mensile: allenamenti (V10) e partite (V16).
 *
 * Tutto lavora su date di calendario come "2026-09-23", mai su istanti del
 * browser: il giorno di un impegno e quello italiano, e la griglia del mese
 * non deve saltare o ripetere un giorno al cambio dell'ora.
 */

import { dataLocale, giornoSettimana, oraLocale } from '@calcio/shared';
import { formatoGiorno, giornoUtc } from './date.js';

/** Il giorno e l'ora da proporre creando dal calendario, come "2026-09-23" e "18:00". */
export interface GiornoProposto {
  data: string;
  ora: string;
}

/** Un impegno nel calendario: basta l'istante. */
interface ConData {
  dataOra: string;
}

/** "2026-09" dal giorno italiano: il filtro per periodo va per mese. */
export const meseDi = (dataOra: string): string => dataLocale(dataOra).slice(0, 7);

const formatoMese = formatoGiorno({ month: 'long', year: 'numeric' });

/** "settembre 2026" da "2026-09". */
export const nomeMese = (mese: string): string => formatoMese.format(giornoUtc(`${mese}-01`));

export interface GiornoGriglia {
  /** "2026-09-23". */
  data: string;
  /** Falso per i giorni del mese prima o dopo, che completano le settimane. */
  delMese: boolean;
}

const due = (n: number) => String(n).padStart(2, '0');

const perData = (t: number) => {
  const d = new Date(t);
  return `${d.getUTCFullYear()}-${due(d.getUTCMonth() + 1)}-${due(d.getUTCDate())}`;
};

const giornoMs = 24 * 60 * 60 * 1000;

/**
 * Le settimane intere che coprono il mese "2026-09", da lunedi a domenica:
 * quattro, cinque o sei righe da sette giorni.
 */
export function grigliaDelMese(mese: string): GiornoGriglia[][] {
  const [a, m] = mese.split('-').map(Number) as [number, number];
  const primo = Date.UTC(a, m - 1, 1);
  const ultimo = Date.UTC(a, m, 0);
  const inizio = primo - (giornoSettimana(perData(primo)) - 1) * giornoMs;
  const fine = ultimo + (7 - giornoSettimana(perData(ultimo))) * giornoMs;

  const settimane: GiornoGriglia[][] = [];
  for (let t = inizio; t <= fine; t += giornoMs) {
    if ((t - inizio) % (7 * giornoMs) === 0) settimane.push([]);
    const data = perData(t);
    settimane.at(-1)!.push({ data, delMese: data.startsWith(mese) });
  }
  return settimane;
}

/** Gli impegni per giorno italiano, in ordine di ora. */
export function perGiorno<T extends ConData>(elenco: readonly T[]): Map<string, T[]> {
  const giorni = new Map<string, T[]>();
  const ordinati = [...elenco].sort(
    (a, b) => new Date(a.dataOra).getTime() - new Date(b.dataOra).getTime(),
  );
  for (const a of ordinati) {
    const giorno = dataLocale(a.dataOra);
    giorni.set(giorno, [...(giorni.get(giorno) ?? []), a]);
  }
  return giorni;
}

/** I mesi toccati dalla stagione, dal primo all'ultimo: "2026-07", "2026-08"… */
export function mesiDellaStagione(stagione: { dataInizio: string; dataFine: string }): string[] {
  const mesi: string[] = [];
  let [a, m] = stagione.dataInizio.split('-').map(Number) as [number, number];
  const ultimo = stagione.dataFine.slice(0, 7);
  for (let mese = `${a}-${due(m)}`; mese <= ultimo; mese = `${a}-${due(m)}`) {
    mesi.push(mese);
    [a, m] = m === 12 ? [a + 1, 1] : [a, m + 1];
  }
  return mesi;
}

/**
 * Il mese da cui parte il calendario: quello richiesto se sta nella stagione,
 * altrimenti quello di oggi; se anche oggi e fuori, il mese della stagione
 * piu vicino, cioe il primo prima dell'inizio e l'ultimo dopo la fine.
 */
export function meseIniziale(
  stagione: { dataInizio: string; dataFine: string },
  adesso: Date,
  richiesto?: string | null,
): string {
  const mesi = mesiDellaStagione(stagione);
  if (richiesto && mesi.includes(richiesto)) return richiesto;
  const corrente = meseDi(adesso.toISOString());
  if (corrente < mesi[0]!) return mesi[0]!;
  if (corrente > mesi.at(-1)!) return mesi.at(-1)!;
  return corrente;
}

/** Il giorno sta nella stagione: solo li si crea un allenamento. */
export const nellaStagione = (data: string, stagione: { dataInizio: string; dataFine: string }) =>
  data >= stagione.dataInizio && data <= stagione.dataFine;

/**
 * La stagione estesa ai mesi degli impegni che ne cadono fuori: una partita
 * non e legata alle date della stagione, e il calendario deve mostrarla.
 */
export function stagioneCon(
  stagione: { dataInizio: string; dataFine: string },
  elenco: readonly ConData[],
): { dataInizio: string; dataFine: string } {
  let { dataInizio, dataFine } = stagione;
  for (const e of elenco) {
    const giorno = dataLocale(e.dataOra);
    if (giorno < dataInizio) dataInizio = giorno;
    if (giorno > dataFine) dataFine = giorno;
  }
  return { dataInizio, dataFine };
}

/**
 * L'ora da proporre creando dal calendario: la piu usata dalla squadra
 * nell'ora italiana, a parita la piu presto; quella data se non ce n'e ancora.
 */
export function oraPiuUsata(elenco: readonly ConData[], senza = '18:00'): string {
  const conteggi = new Map<string, number>();
  for (const a of elenco) {
    const ora = oraLocale(a.dataOra);
    conteggi.set(ora, (conteggi.get(ora) ?? 0) + 1);
  }
  let scelta = senza;
  let volte = 0;
  for (const [ora, n] of [...conteggi].sort(([x], [y]) => x.localeCompare(y))) {
    if (n > volte) [scelta, volte] = [ora, n];
  }
  return scelta;
}
